/* eslint-disable react-refresh/only-export-components */
import React, { createContext, useContext, useState, useEffect, useLayoutEffect, useCallback } from 'react';
import { MotionConfig } from 'framer-motion';
import { useAuth } from './AuthContext';
import { useTheme } from './ThemeContext';
import { useAppearancePreferences } from '@/hooks/useAppearancePreferences';
import type { AppearanceSettings } from '../services/UserSettingsService';

// Default appearance settings
const DEFAULT_APPEARANCE: AppearanceSettings = {
  themeMode: 'system',
  density: 'comfortable',
  homePage: 'dashboard',
  language: 'en',
  dateFormat: 'MM/DD/YYYY',
  animations: true,
};

interface AppearanceContextType {
  // Settings values
  density: AppearanceSettings['density'];
  language: AppearanceSettings['language'];
  dateFormat: AppearanceSettings['dateFormat'];
  animations: boolean;
  homePage: AppearanceSettings['homePage'];
  
  // Update methods (for Settings page to sync)
  updateFromSettings: (settings: Partial<AppearanceSettings>) => void;
  
  // Date formatting helper
  formatDate: (date: Date | string | number) => string;
}

const AppearanceContext = createContext<AppearanceContextType | undefined>(undefined);

export function AppearanceProvider({ children }: { children: React.ReactNode }) {
  const { user } = useAuth();
  const userId = user?.id || 'anonymous';
  const { setTheme } = useTheme();
  const { appearance: storedAppearance, raw } = useAppearancePreferences(userId);
  
  const [density, setDensity] = useState<AppearanceSettings['density']>(DEFAULT_APPEARANCE.density);
  const [language, setLanguage] = useState<AppearanceSettings['language']>(DEFAULT_APPEARANCE.language);
  const [dateFormat, setDateFormat] = useState<AppearanceSettings['dateFormat']>(DEFAULT_APPEARANCE.dateFormat);
  const [animations, setAnimations] = useState<boolean>(DEFAULT_APPEARANCE.animations);
  const [homePage, setHomePage] = useState<AppearanceSettings['homePage']>(DEFAULT_APPEARANCE.homePage);

  // Appearance is browser-local. Loading it must not bootstrap notification
  // delivery preferences or let a previous actor's asynchronous read win.
  useLayoutEffect(() => {
    setDensity(storedAppearance.density);
    setLanguage(storedAppearance.language);
    setDateFormat(storedAppearance.dateFormat);
    setAnimations(storedAppearance.animations);
    setHomePage(storedAppearance.homePage);
  }, [storedAppearance, raw, userId]);

  useLayoutEffect(() => {
    setTheme(storedAppearance.themeMode);
  }, [setTheme, storedAppearance.themeMode, userId]);

  // Apply density classes to root element
  useEffect(() => {
    const root = document.documentElement;
    
    // Remove existing density classes
    root.classList.remove('density-compact', 'density-comfortable', 'density-spacious');
    
    // Add current density class
    root.classList.add(`density-${density}`);
    
    // Also set CSS custom properties for components that need them
    const densityValues = {
      compact: { spacing: '0.5rem', padding: '0.5rem', gap: '0.5rem' },
      comfortable: { spacing: '1rem', padding: '1rem', gap: '0.75rem' },
      spacious: { spacing: '1.5rem', padding: '1.5rem', gap: '1rem' },
    };
    
    const values = densityValues[density];
    root.style.setProperty('--density-spacing', values.spacing);
    root.style.setProperty('--density-padding', values.padding);
    root.style.setProperty('--density-gap', values.gap);
  }, [density]);

  // Apply animations preference to root element
  useEffect(() => {
    const root = document.documentElement;
    
    if (animations) {
      root.classList.remove('reduce-motion');
    } else {
      root.classList.add('reduce-motion');
    }
  }, [animations]);

  // Apply language as data attribute (for potential future i18n)
  useEffect(() => {
    document.documentElement.setAttribute('lang', language.split('-')[0]);
    document.documentElement.setAttribute('data-locale', language);
  }, [language]);

  // Method for Settings page to sync updates
  const updateFromSettings = useCallback((settings: Partial<AppearanceSettings>) => {
    if (settings.density !== undefined) setDensity(settings.density);
    if (settings.language !== undefined) setLanguage(settings.language);
    if (settings.dateFormat !== undefined) setDateFormat(settings.dateFormat);
    if (settings.animations !== undefined) setAnimations(settings.animations);
    if (settings.homePage !== undefined) setHomePage(settings.homePage);
  }, []);

  // Date formatting function
  const formatDate = useCallback((date: Date | string | number): string => {
    const d = new Date(date);
    
    if (isNaN(d.getTime())) {
      return 'Invalid date';
    }
    
    const day = String(d.getDate()).padStart(2, '0');
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const year = d.getFullYear();
    
    switch (dateFormat) {
      case 'DD/MM/YYYY':
        return `${day}/${month}/${year}`;
      case 'YYYY-MM-DD':
        return `${year}-${month}-${day}`;
      case 'MM/DD/YYYY':
      default:
        return `${month}/${day}/${year}`;
    }
  }, [dateFormat]);

  return (
    <AppearanceContext.Provider
      value={{
        density,
        language,
        dateFormat,
        animations,
        homePage,
        updateFromSettings,
        formatDate,
      }}
    >
      <MotionConfig reducedMotion={animations ? 'user' : 'always'}>{children}</MotionConfig>
    </AppearanceContext.Provider>
  );
}

export function useAppearance() {
  const context = useContext(AppearanceContext);
  if (context === undefined) {
    // Return a safe fallback instead of throwing - allows use during SSR or before provider mounts
    console.warn('[useAppearance] Used outside AppearanceProvider, returning defaults');
    return {
      density: 'comfortable' as const,
      language: 'en' as const,
      dateFormat: 'MM/DD/YYYY' as const,
      animations: true,
      homePage: 'dashboard' as const,
      updateFromSettings: () => {},
      formatDate: (date: Date | string | number) => new Date(date).toLocaleDateString(),
    };
  }
  return context;
}

/**
 * Hook to format dates using user's preferred format
 */
export function useDateFormat() {
  const { formatDate, dateFormat } = useAppearance();
  return { formatDate, dateFormat };
}

/**
 * Hook to get density setting
 */
export function useDensity() {
  const { density } = useAppearance();
  return density;
}

/**
 * Hook to check if animations are enabled
 */
export function useAnimations() {
  const { animations } = useAppearance();
  return animations;
}

/**
 * Hook to get default home page
 */
export function useHomePage() {
  const { homePage } = useAppearance();
  return homePage;
}
